import { useEffect, useState } from "react";
import { Globe, X } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const HINT_KEY = "pula_lang_hint_seen";
const PROMPTED_KEY = "pula_lang_prompted";

// A gentle, one-time nudge telling members the whole site can follow their language.
// Shown only when the auto-detect prompt won't (English browsers), and never nags twice.
export default function LanguageHint() {
  const { language } = useSettings();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      if (localStorage.getItem(HINT_KEY) === "1") return;
      if (localStorage.getItem(PROMPTED_KEY) === "1") return; // auto-detect prompt is handling this visitor
      if (language && language !== "en") { localStorage.setItem(HINT_KEY, "1"); return; }
      setShow(true);
    }, 2500);
    return () => clearTimeout(t);
  }, [language]);

  const dismiss = () => {
    localStorage.setItem(HINT_KEY, "1");
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="language-hint"
      data-no-translate
      className="fixed top-14 right-3 z-[65] max-w-[17rem] rounded-xl border border-[var(--pula-border)] bg-white shadow-xl p-3 animate-in fade-in slide-in-from-top-2"
    >
      <span className="absolute -top-1.5 right-8 w-3 h-3 rotate-45 bg-white border-l border-t border-[var(--pula-border)]" />
      <button
        data-testid="language-hint-close"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-1.5 right-1.5 p-1 rounded-full text-pula-inksoft hover:bg-black/5 transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-start gap-2 pr-4">
        <span className="w-7 h-7 rounded-full bg-pula-green/15 grid place-items-center text-pula-green shrink-0">
          <Globe className="w-4 h-4" />
        </span>
        <div>
          <p className="text-xs font-body text-pula-ink leading-snug">
            Tap the <b>globe</b> up here to change language — the whole site follows you.
          </p>
          <button
            data-testid="language-hint-got-it"
            onClick={dismiss}
            className="mt-2 px-2.5 py-1 rounded-full bg-pula-green hover:bg-pula-greenhover text-white text-xs font-body font-medium transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
